import React, { useCallback, useEffect, useState } from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import { useSelector } from 'react-redux'
import { isEmpty, mergeAll } from 'ramda'
import useCategories from 'commons/useCategories'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { Typography, makeStyles } from '@material-ui/core'

import { isEmptyOrNil } from 'libs/empty'
import { SECONDARY } from 'app/constants/button'
import { AVAILABLE } from 'app/constants/statusColors'

import PanelsInfo from 'app/componentV4/Panels/Info'
import LinkSignout from 'app/componentV4/Link/LinkSignout'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
// import LabelError from 'app/componentV4/Labels/LabelError'
// import MapOnboard from './MapContainer'

import GooglePlace from '../v4/GooglePlace'
import { useAutoSelectCategories } from './useAutoSelectCategories'
import MapContainer from './MapContainer'
import Map from './Map'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => {
  const mobile = theme.breakpoints.down(900)
  const desktop = theme.breakpoints.up(900)

  return {
    input: {
      '&.MuiOutlinedInput-root': {
        height: '48px !important',
      },
    },
    groupLabel: { fontWeight: 700, color: '#000' },
    informationDialog: {
      '& .MuiDialog-paper': {
        width: '450px !important',
      },
    },
    map: {
      [mobile]: { display: 'block' },
      [desktop]: {
        display: 'none',
      },
    },
  }
})

const Step1 = ({ initial, onSubmit, isLoading }) => {
  const classes = useStyles()
  const category = useCategories()
  const categoryList = useSelector((state) => state.categories.categorieList)

  const [categories, setCategories] = useState([])
  const { selectCategory } = useAutoSelectCategories(categories)

  const handleCategories = useCallback(async () => {
    await category.categories.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEmpty(categoryList)) {
      handleCategories()
    } else setCategories(category.mapGroupCategories(categoryList))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryList])

  return (
    <Formik
      initialValues={{
        address: initial.address,
        category: initial.category,
        location: initial.location,
        placeName: initial.placeName,
        googlePlaceId: initial.googlePlaceId,
      }}
      validationSchema={Yup.object({
        placeName: Yup.string().required('This field is required'),
        category: Yup.object().required('This field is required'),
        address: Yup.string().required('This field is required'),
        location: Yup.object().required(
          'We couldn’t find this address. Please enter a valid address or use the map tool to set your location.',
        ),
      })}
      onSubmit={(values) => {
        onSubmit(mergeAll([initial, values]))
      }}
      enableReinitialize
    >
      {({ values, errors, touched, handleBlur, setFieldValue }) => (
        <MapContainer
          location={values.location}
          onLocationChange={(data) => {
            setFieldValue('location', data.location)
          }}
        >
          <div className="overflow-auto">
            <Typography className="font-500 text-16 text-primary">
              Welcome to MessageSpring
            </Typography>

            <PanelsInfo
              type={AVAILABLE}
              className="my-10 flex space-x-3 items-center"
            >
              <Typography className="text-14">
                <strong>{`“Place”`}</strong>
                {
                  'refers to your business or organization name and serves as your unique identity.'
                }
              </Typography>
            </PanelsInfo>
            <Form>
              <LabelInput
                label="What would you like to name your Place?"
                required
              >
                <GooglePlace
                  id="on-board-place-name"
                  name="placeName"
                  className={classes.input}
                  value={values.placeName}
                  placeholder="Place name"
                  onChange={(value) => {
                    const isFromCurated = value.googlePlaceId
                    const isFormikCurated = values.googlePlaceId
                    const isManual = !isFromCurated && isFormikCurated // Curated to Manual

                    if (!isFromCurated && !isFormikCurated) {
                      // Manual to Manual
                      setFieldValue('placeName', value)
                      return
                    }

                    setFieldValue(
                      'placeName',
                      isManual ? value : value.displayName,
                    )
                    setFieldValue('address', isManual ? '' : value.address)
                    setFieldValue(
                      'googlePlaceId',
                      isManual ? '' : value.googlePlaceId,
                    )
                    setFieldValue(
                      'category',
                      isManual ? null : selectCategory(value.types),
                    )

                    if (isFromCurated) {
                      setFieldValue('location', value.location)
                    }
                  }}
                  maxLength={100}
                  inputProps={{
                    maxLength: 100,
                    'aria-label': 'onBoard placeName',
                  }}
                  onBlur={handleBlur}
                  error={touched.placeName && Boolean(errors.placeName)}
                  helperText={touched.placeName && errors.placeName}
                />
              </LabelInput>

              <LabelInput
                label={'What category best describes your Place?'}
                required
              >
                <Autocomplete
                  id="on-board-autocomplete-category"
                  name="category"
                  options={categories}
                  placeholder={'Place category'}
                  onChange={(event, newValue) => {
                    setFieldValue('category', newValue)
                    !isEmptyOrNil(values.googlePlaceId) && // If update data it's not from google and need clearly data from google
                      setFieldValue('googlePlaceId', '')
                  }}
                  onBlur={handleBlur}
                  getOptionLabel={(option) =>
                    `${option.categoryGroupName}/${option.label}`
                  }
                  groupBy={(option) => option.categoryGroupName}
                  classes={{
                    groupLabel: classes.groupLabel,
                  }}
                  value={values.category}
                  renderOption={(option) => option.label}
                  getOptionSelected={(option, value) => {
                    option.id === value.id
                  }}
                  renderInput={(params) => (
                    <div ref={params.InputProps.ref} className="w-full">
                      <SimpleInput
                        className={classes.input}
                        id="on-board-input-category"
                        placeholder={'Place category'}
                        size="medium"
                        fullWidth
                        error={touched.category && Boolean(errors.category)}
                        helperText={touched.category && errors.category}
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'onBoard Category',
                        }}
                      />
                    </div>
                  )}
                />
              </LabelInput>

              <div className={clsx('my-10', classes.map)}>
                <Map
                  id="on-board-googlemap-mobile"
                  className="h-[300px] w-full relative"
                  location={values.location}
                  onLocationChange={(data) => {
                    setFieldValue('location', data.location)
                  }}
                />
              </div>

              <LabelInput label={'Where is your Place located?'} required>
                <GooglePlace
                  id="on-board-located"
                  name="address"
                  className={classes.input}
                  value={values.address}
                  placeholder="Place location"
                  onChange={(value) => {
                    const isManualInput = !value.googlePlaceId
                    setFieldValue('googlePlaceId', '')

                    if (isManualInput) {
                      setFieldValue('address', value)
                    } else {
                      setFieldValue('address', value.address)
                      setFieldValue('location', value.location)
                    }
                  }}
                  maxLength={199}
                  inputProps={{
                    maxLength: 199,
                    'aria-label': 'onBoard Located',
                  }}
                  onBlur={handleBlur}
                  error={
                    (touched.location && errors.location) ||
                    (touched.address && Boolean(errors.address))
                  }
                  helperText={
                    (touched.location && errors.location) ||
                    (touched.address && errors.address)
                  }
                />
              </LabelInput>

              <div className="flex justify-center mt-10">
                <ContainedButton
                  id="on-board-step1-submit"
                  type="submit"
                  color={SECONDARY}
                  className="w-full h-48"
                  disabled={isLoading}
                  isLoading={isLoading}
                >
                  {'Next'}
                </ContainedButton>
              </div>
            </Form>
            <LinkSignout />
          </div>
        </MapContainer>
      )}
    </Formik>
  )
}

Step1.propTypes = {
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  initial: PropTypes.shape({
    address: PropTypes.string,
    category: PropTypes.object,
    location: PropTypes.object,
    placeName: PropTypes.string,
    googlePlaceId: PropTypes.string,
  }).isRequired,
}

export default Step1
